import {
  differenceInCalendarDays,
  differenceInHours,
  differenceInMinutes,
  format,
  isWithinInterval,
  subDays,
  subHours,
  subMinutes,
  subYears,
} from "date-fns";

import {
  Person,
  TaskManipulator,
  TaskManipulatorType,
} from "@fartherfinance/frontend/api/Tasks/Types";
import { AdvisorId } from "@fartherfinance/frontend/api/Types";

import { UNKNOWN_USER } from "@src/multiCustodian/pages/Advisor/common";
import { pluralize } from "@src/utils/pluralize";

export const getDateFormatYearInsensitive = (): string => {
  return "eeee',' MMMM do 'at' K:mmaaa";
};

export const getPersonName = (
  person: Person,
  advisorId?: AdvisorId
): string => {
  // NOTE: if we are the task actor just show 'You' rather than my full name
  return person.id === advisorId
    ? "You"
    : person.name
    ? `${person.name.first} ${person.name.last}`
    : UNKNOWN_USER;
};

export const getPersonNameV2 = (person: Person): string => {
  return person.name
    ? `${person.name.first} ${person.name.last}`
    : UNKNOWN_USER;
};

export const getManipulatorName = (
  manipulator: TaskManipulator,
  advisorId?: AdvisorId
): string => {
  return manipulator.type === TaskManipulatorType.enum.SYSTEM
    ? "System"
    : getPersonName(manipulator, advisorId);
};

export const getManipulatorNameV2 = (manipulator: TaskManipulator): string => {
  return manipulator.type === TaskManipulatorType.enum.SYSTEM
    ? "System"
    : getPersonNameV2(manipulator);
};

export const formatRelativeTimestamp = (date: Date): string => {
  const now = new Date();
  const longTimeAgo = subYears(now, 10);
  const sevenDaysAgo = subDays(now, 7);
  const dayAgo = subDays(now, 1);
  const hourAgo = subHours(now, 1);
  const minuteAgo = subMinutes(now, 1);

  if (isWithinInterval(date, { start: longTimeAgo, end: sevenDaysAgo })) {
    return format(date, "M/d/yy"); // Example: 1/1/24
  }

  if (isWithinInterval(date, { start: sevenDaysAgo, end: dayAgo })) {
    return `${pluralize(differenceInCalendarDays(now, date), "day", true)} ago`; // Example: 5 days ago
  }

  if (isWithinInterval(date, { start: dayAgo, end: hourAgo })) {
    return `${pluralize(differenceInHours(now, date), "hour", true)} ago`; // Example: 3 hours ago
  }

  if (isWithinInterval(date, { start: hourAgo, end: minuteAgo })) {
    return `${pluralize(differenceInMinutes(now, date), "minute", true)} ago`; // Example: 45 minutes ago
  }

  if (isWithinInterval(date, { start: minuteAgo, end: now })) {
    return `Just now`;
  }

  return "";
};
